import { Slider, Box, Typography } from "@mui/material";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { palette, spacing } from "../../../styles/theme";
import { FlexColBox } from "..";

const defaultRanges = [
  { min: 1, max: 20, weight: 10, step: 1 },
  { min: 20, max: 30, weight: 15, step: 1 },
  { min: 30, max: 50, weight: 15, step: 1 },
  { min: 50, max: 150, weight: 20, step: 5 },
  { min: 150, max: 500, weight: 15, step: 25 },
  { min: 500, max: 1500, weight: 10, step: 100 },
  { min: 1500, max: 5000, weight: 15, step: 500 },
];

const defaultBreakpoints = [1, 20, 30, 50, 150, 500, 1500, 5000];

interface Props {
  onChange?: (value: number) => void;
  value?: number;
  ranges?: { min: number; max: number; weight: number; step: number }[];
  breakpoints?: number[];
}

const NonLinearSlider = ({
  onChange,
  ranges = defaultRanges,
  breakpoints = defaultBreakpoints,
  ...props
}: Props) => {
  const [value, setValue] = useState<number>(props.value || 1);
  const sliderRef = useRef<HTMLDivElement>(null);

  //weight = % of slider width, step = step of slider, min = min value, max = max value

  // Convert value to slider position (0-100)
  const valueToPosition = useCallback(
    (val: number): number => {
      let position = 0;

      for (const range of ranges) {
        if (val <= range.min) break;

        if (val >= range.max) {
          position += range.weight;
        } else {
          const rangeProgress = (val - range.min) / (range.max - range.min);
          position += rangeProgress * range.weight;
          break;
        }
      }

      return position;
    },
    [ranges]
  );

  // Convert slider position (0-100) to value
  const positionToValue = useCallback(
    (position: number): number => {
      let accumulatedWeight = 0;

      for (const range of ranges) {
        if (position <= accumulatedWeight + range.weight) {
          const rangeProgress = (position - accumulatedWeight) / range.weight;
          const rawValue = range.min + rangeProgress * (range.max - range.min);

          // Special handling for first range (1-20)
          if (range.min === 1 && range.max === 20) {
            if (rawValue <= 2) return 1; // Return 1 if value is close to the start
            // Calculate nearest even number between 2-20
            const evenValue = Math.round((rawValue - 2) / 2) * 2 + 2;
            return Math.min(20, Math.max(2, evenValue));
          }

          // Round to nearest step
          const stepsInRange = Math.round((rawValue - range.min) / range.step);
          return Math.min(range.max, range.min + stepsInRange * range.step);
        }
        accumulatedWeight += range.weight;
      }

      return ranges[ranges.length - 1].max;
    },
    [ranges]
  );

  const handleSliderChange = useCallback(
    (newPosition: number | number[]) => {
      const pos = Array.isArray(newPosition) ? newPosition[0] : newPosition;
      const newValue = positionToValue(pos);
      setValue(newValue);
      onChange?.(newValue);
    },
    [positionToValue, onChange]
  );

  useEffect(() => {
    if (props.value !== undefined && props.value !== value) {
      setValue(props.value);
    }
  }, [props.value, value]);

  return (
    <FlexColBox sx={{ width: "100%", gap: spacing.smallDesktop }}>
      <Box
        ref={sliderRef}
        sx={{
          position: "relative",
          px: spacing.smallDesktop,
        }}
      >
        <Slider
          value={valueToPosition(value)}
          onChange={(_, newPosition) => handleSliderChange(newPosition)}
          min={0}
          max={100}
          step={0.1}
          marks={breakpoints.map((point) => ({
            value: valueToPosition(point),
            label: "",
          }))}
          sx={{
            width: "100%",
            [`& .MuiSlider-mark[data-index="${ranges.length}"], & .MuiSlider-mark[data-index="0"]`]:
              {
                backgroundColor: "transparent !important",
              },
            "& .MuiSlider-thumb": {
              height: 16,
              width: 16,
              backgroundColor: palette.white,
            },
            "& .MuiSlider-track": {
              height: 8,
              backgroundColor: palette.blue.secondary,
              border: "none",
            },
            "& .MuiSlider-rail": {
              height: 8,
              backgroundColor: palette.grey.lightBackground,
              opacity: 1,
              border: `1px solid ${palette.grey.midGrey}`,
            },
            "& .MuiSlider-mark": {
              backgroundColor: palette.grey.darkGrey,
              height: 8,
              width: "1px",
              "&.MuiSlider-markActive": {
                backgroundColor: palette.blue.secondary,
                opacity: 1,
              },
            },
            "& .MuiSlider-markLabel": {
              display: "none",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          position: "relative",
          height: "1.5rem",
          width: "100%",
          px: spacing.smallDesktop,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
          }}
        >
          {breakpoints.map((point, index) => (
            <Box
              key={index}
              component="span"
              sx={{
                position: "absolute",
                left: `${valueToPosition(point)}%`,
                transform:
                  index === breakpoints.length - 1
                    ? "translateX(-100%)" // Align last label to the right edge
                    : index === 0
                      ? "translateX(0%)" // Align first label to the left edge
                      : "translateX(-50%)", // Center align all other labels
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                variant="actionSmallIcon"
                color={palette.grey.primary}
              >
                {point === 5000 ? "5,000+" : point.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </FlexColBox>
  );
};
export default NonLinearSlider;
